import { useEffect, useState } from 'react'
import { Field, Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { ApiError, explain } from '@/shared/api/problem'
import type { PartyDto } from '@/shared/api/types'
import { CODE_LIST_UNAVAILABLE, usePoliticsCodes } from '@/features/polling/useVocabulary'
import { createParty, updateParty } from './api'
import { Swatch, partyStatusLabel } from './vocab'
import { DatePicker } from '@/components/erp/date-picker'

/**
 * Register a party, or correct one.
 *
 * ## The colour field is not decoration
 *
 * `colourHex` is read straight into every chart, chip and swatch that colours by
 * party — there is no name→colour table anywhere in the app to fall back on. A
 * party with no colour renders with a hollow swatch everywhere, which is honest
 * and ugly, so the field says what it is for.
 *
 * ## Both refusals are the server's
 *
 * The code is unique per register and case-insensitively so (409
 * `politics/code-taken`), and the colour must be six hex digits after a hash
 * (400). Neither is re-implemented here: the form submits and renders what comes
 * back, because a client rule that disagrees with the server is a rule that
 * silently blocks something legal.
 */
export function PartyFormDialog({ open, party, onClose, onSaved }: {
  open: boolean
  /** `null` creates. A party edits in place and keeps its id. */
  party: PartyDto | null
  onClose: () => void
  onSaved: (saved: PartyDto) => void
}) {
  const editing = party !== null

  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [shortName, setShortName] = useState('')
  const [colourHex, setColourHex] = useState('')
  const statuses = usePoliticsCodes('partyStatus')
  const [statusCode, setStatusCode] = useState<string>('ACTIVE')
  const [registeredOn, setRegisteredOn] = useState('')
  const [deregisteredOn, setDeregisteredOn] = useState('')
  const [notes, setNotes] = useState('')

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  // Re-seeded whenever the dialog opens on a different record. Without the
  // `open` dependency an edit dialog closed and reopened on another row would
  // still be holding the first row's values.
  useEffect(() => {
    if (!open) return
    setCode(party?.code ?? '')
    setName(party?.name ?? '')
    setShortName(party?.shortName ?? '')
    setColourHex(party?.colourHex ?? '')
    setStatusCode(party?.statusCode ?? 'ACTIVE')
    setRegisteredOn(party?.registeredOn ?? '')
    setDeregisteredOn(party?.deregisteredOn ?? '')
    setNotes(party?.notes ?? '')
    setError(null)
    setFieldErrors({})
  }, [open, party])

  async function submit() {
    setBusy(true)
    setError(null)
    setFieldErrors({})
    const body = {
      code: code.trim(),
      name: name.trim(),
      shortName: shortName.trim() || null,
      colourHex: colourHex.trim() || null,
      statusCode,
      registeredOn: registeredOn || null,
      deregisteredOn: deregisteredOn || null,
      notes: notes.trim() || null,
      // Not edited here — no upload flow — but a PUT replaces the whole record,
      // so omitting it would blank the symbol of every party anybody edits.
      symbolFileId: party?.symbolFileId ?? null,
    }
    try {
      const saved = editing
        ? await updateParty(party.id, body)
        : await createParty(body)
      onSaved(saved)
    } catch (e) {
      if (e instanceof ApiError) setFieldErrors(e.fieldErrors())
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  const ready = code.trim().length > 0 && name.trim().length > 0

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Edit ${party.code}` : 'New party'}
      description={editing
        ? 'The code is how every candidate, chart and coalition membership refers to this party.'
        : 'A party joins a coalition afterwards, from its own panel — one membership at a time.'}
      width="md"
      footer={(
        <>
          <button type="button" className="erp-btn-ghost h-9 text-erp-sm" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="erp-btn erp-btn-primary h-9 text-erp-sm"
            disabled={!ready || busy} onClick={submit}>
            {busy ? 'Saving…' : editing ? 'Save' : 'Register'}
          </button>
        </>
      )}
    >
      <div className="space-y-3">
        <ErrorStrip message={error} />

        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-4">
            <Field label="Code" required error={fieldErrors.code}
              hint="Uppercased by the server.">
              <input className="erp-input w-full font-mono uppercase" value={code} maxLength={32}
                onChange={(e) => setCode(e.target.value)} placeholder="ODM" />
            </Field>
          </div>
          <div className="col-span-8">
            <Field label="Name" required error={fieldErrors.name}>
              <input className="erp-input w-full" value={name} maxLength={200}
                onChange={(e) => setName(e.target.value)}
                placeholder="Orange Democratic Movement" />
            </Field>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-6">
            <Field label="Short name" error={fieldErrors.shortName}
              hint="What the chips and the charts use when there is no room.">
              <input className="erp-input w-full" value={shortName} maxLength={32}
                onChange={(e) => setShortName(e.target.value)} placeholder="ODM" />
            </Field>
          </div>
          <div className="col-span-6">
            <Field label="Status"
              error={fieldErrors.statusCode
                ?? (statuses.unavailable ? CODE_LIST_UNAVAILABLE : undefined)}>
              <select className="erp-input w-full" value={statusCode}
                disabled={statuses.loading || statuses.unavailable}
                onChange={(e) => setStatusCode(e.target.value)}>
                {statuses.loading && <option value={statusCode}>Loading…</option>}
                {statuses.codes.map((s) => (
                  <option key={s} value={s}>{partyStatusLabel(s)}</option>
                ))}
                {/* The status this party already carries, kept selectable even if
                    the served list no longer offers it. A record whose status was
                    retired server-side must not silently change status because a
                    form could not represent it. */}
                {statusCode !== '' && !statuses.codes.includes(statusCode) && (
                  <option value={statusCode}>{partyStatusLabel(statusCode)}</option>
                )}
              </select>
            </Field>
          </div>
        </div>

        <Field label="Colour" error={fieldErrors.colourHex}
          hint="Six hex digits after a hash. Every chart that colours by party reads this and nothing else — there is no fallback palette.">
          <div className="flex items-center gap-2">
            <input
              type="color"
              aria-label="Pick the party colour"
              className="h-9 w-12 rounded-md border border-input bg-background p-1"
              value={/^#[0-9a-fA-F]{6}$/.test(colourHex) ? colourHex : '#888888'}
              onChange={(e) => setColourHex(e.target.value)}
            />
            <input className="erp-input flex-1 font-mono" value={colourHex} maxLength={7}
              onChange={(e) => setColourHex(e.target.value)} placeholder="#f97316" />
            <Swatch colourHex={colourHex} className="h-5 w-5" />
            {colourHex && (
              <button type="button" className="erp-btn-ghost h-9 text-erp-xs"
                onClick={() => setColourHex('')}>
                No colour
              </button>
            )}
          </div>
        </Field>

        <div className="grid grid-cols-12 gap-3">
          <div className="col-span-6">
            <Field label="Registered on" error={fieldErrors.registeredOn}>
              <DatePicker value={registeredOn} onChange={setRegisteredOn} className="w-full" />
            </Field>
          </div>
          <div className="col-span-6">
            <Field label="Deregistered on" error={fieldErrors.deregisteredOn}
              hint="Leave empty while the party is on the register.">
              <DatePicker value={deregisteredOn} onChange={setDeregisteredOn} className="w-full" />
            </Field>
          </div>
        </div>

        <Field label="Notes" error={fieldErrors.notes}>
          <textarea className="erp-input w-full min-h-[72px] py-2" value={notes} maxLength={4000}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Registrar correspondence, compliance status, anything a reader of this row would need." />
        </Field>

        {!editing && (
          <Note tone="info">
            Coalition membership is recorded separately, from the party's own panel. A party holds
            one open membership at a time and the history keeps every closed one.
          </Note>
        )}
      </div>
    </Modal>
  )
}
